import type { Address } from "@/services/addresses";
import type { UserBankInfo } from "@/services/user/bankInfo";
import { formatIban, normalizeIban } from "@/services/user/bankInfo";
import { assertUserFlagsAreUnique, type UserSettings } from "@/services/user/settings";
import type { UserFlag } from "@/services/user/settings/flags";

const userSettings = ref<UserSettings>();
const userBankInfo = ref<UserBankInfo>();
const userAddress = ref<Address>();

export function useUserSettings() {
  const userHasFlag = (flag: UserFlag): boolean => {
    return userSettings.value?.flags.includes(flag) ?? false;
  };

  const removeUserFlag = (flag: UserFlag): void => {
    userSettings.value?.flags.splice(userSettings.value?.flags.indexOf(flag), 1);
  };

  // Extra validator to be sure
  watch(userSettings, (newSetting) => {
    if (newSetting !== undefined) {
      assertUserFlagsAreUnique(newSetting.flags);
    }
  });

  // -----------------------------------
  // Bank info
  // -----------------------------------

  const formattedPrimaryAccountNumber = computed(() => {
    const bank = userBankInfo.value;
    if (bank === undefined) {
      return undefined;
    } else if (bank.iban !== "") {
      return formatIban(normalizeIban(bank.iban));
    } else if (bank.accountNumber !== "") {
      return bank.accountNumber;
    } else {
      return undefined;
    }
  });

  // -----------------------------------
  // Shortcuts
  // -----------------------------------

  const nickname = computed(() => userSettings.value?.nickname);

  return {
    userSettings,
    userBankInfo,
    userAddress,
    nickname,

    formattedPrimaryAccountNumber,

    userHasFlag,
    removeUserFlag,
  };
}
